import * as launchDarklyClient from 'launchdarkly-js-client-sdk';
import type { LDClient as LDClientInterface } from 'launchdarkly-js-client-sdk';
import { createContext, useEffect, useState, PropsWithChildren } from 'react';
import { LD_CLIENT_ID } from '~/utils/constants';
import { store } from '~/utils/store';

export const LDContext = createContext({} as LDClientInterface);
export const LD_KEY = 'flex'; // https://docs.launchdarkly.com/home/users/anonymous-users/?q=anonymous

// stub -Starting with version 3.x.x and abobe LD expects context to be passed instead of user. The context can be changed at any time using identify().
const context = {
  key: store.get('USER_ID') ? String(store.get('USER_ID')) : LD_KEY,
  kind: 'user',
  custom: {
    funnel: store.get('ACTIVE_FUNNEL_ID') || '',
    utilityId: store.get('UTILITY_SELECTION')?.id || '',
  },
  funnel: store.get('ACTIVE_FUNNEL_ID') || '',
  utilityId: store.get('UTILITY_SELECTION')?.id || '',
};

export const LDClient = launchDarklyClient.initialize(LD_CLIENT_ID, context, {
  bootstrap: 'localStorage',
});

// Function to update LaunchDarkly context with new utility ID
export const updateLDContextWithUtility = (utilityId: number) => {
  return LDClient.identify({
    key: store.get('USER_ID') ? String(store.get('USER_ID')) : LD_KEY,
    kind: 'user',
    custom: {
      funnel: store.get('ACTIVE_FUNNEL_ID') || '',
      utilityId: utilityId || '',
    },
    funnel: store.get('ACTIVE_FUNNEL_ID') || '',
    utilityId: utilityId || '',
  });
};

export const LDProvider = ({
  children,
  client = LDClient,
}: PropsWithChildren<{ client: LDClientInterface }>) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    client.waitUntilReady().then(() => {
      setReady(true);
    });
    const handleReady = () => setReady(true);
    client.on('ready', handleReady); // Keep as backup

    return () => {
      client.off('ready', handleReady);
    };
  }, [client]);

  if (!ready) return null;

  return <LDContext.Provider value={client}>{children}</LDContext.Provider>;
};
